import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuill } from 'react-quilljs'
import { useParams } from 'react-router-dom'
import { LoadingOutlined } from '@ant-design/icons'
import {
  Checkbox,
  Dropdown,
  Form,
  Input,
  List,
  notification,
  Select,
  Typography
} from 'antd'
import axios from 'axios'
import clsx from 'clsx'
import Const from 'constants/constant'
import dayjs from 'dayjs'
import useDirectory from 'hooks/useDirectory'
import useGetAnnotationColor from 'hooks/useGetAnnotationColor'
import useMutation from 'hooks/useMutation'
import ViewerFilePreview from 'pages/building/buildingDetailViewMode/components/ViewerFilePreview'
import Quill from 'quill'
import CustomBlotFormatter from 'quill/CustomBlotFormatter'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import Api from 'services/api'
import {
  hasLinkErrorState,
  isRefetchingAnnotationsState,
  revalidatePointDetailState,
  SelectedMode,
  selectedPointDetail,
  setAnnotationModeState
} from 'store/buildingStore'
import useSWR, { useSWRConfig } from 'swr'
import {
  AnnotationType,
  BuildingChildrenFolder,
  BuildingFile,
  LinkAnnotation,
  ListBuildingFiles,
  SetAnnotation
} from 'types/building'
import { KrpanoInterface } from 'types/krpano'
import Resizer from 'utils/imageResizer'
import Krpano from 'utils/krpano'
import registerQuillImageHandler from 'utils/quillUtils'
import Validators from 'utils/validators'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import Title from 'components/atoms/Title'
import ChildrenFolderIcon from 'components/icons/ChildrenFolderIcon'
import DropdownIconCustom from 'components/icons/DropdownIconCustom'
import EllipsisIcon from 'components/icons/EllipsisIcon'
import LeftArrowIcon from 'components/icons/LeftArrowIcon'
import RightArrowIcon from 'components/icons/RightArrowIcon'

import useFileDuplicateCheck from '../../hooks/useFileDuplicateCheck'
import DuplicateFileModal from '../DuplicateFileModal'
import ModalEditFileName from '../ModalEditFileName'
import ModalFileInfo from '../ModalFileInfo'
import PreviewFile from '../PreviewFile'

export default function ModalAddSetAnnotation() {
  const { id } = useParams()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const name = Form.useWatch('name', form)
  const inputLinks = Form.useWatch('links', form)
  const memo = Form.useWatch('memo', form)
  const scrollRef = useRef<HTMLDivElement>(null)
  const prevLinksLength = useRef(0)
  const setHasLinkError = useSetRecoilState(hasLinkErrorState)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
  }, [])

  useEffect(() => {
    if (
      inputLinks &&
      prevLinksLength.current > 0 &&
      inputLinks.length > prevLinksLength.current
    ) {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }
    }
    prevLinksLength.current = inputLinks?.length || 0
  }, [inputLinks])

  const { quill, quillRef } = useQuill({
    theme: 'snow',
    modules: Const.QUILL_MODULES,
    formats: Const.QUILL_FORMATS,
    placeholder: '入力してください'
  })

  const { getBuiildingFolderList, getAnnotationList, getBuiildingType } =
    useDirectory()
  const { getAnnotationBgColor } = useGetAnnotationColor()

  const stopPropagation = useRef<boolean>(false)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingContent, setIsLoadingContent] = useState(false)
  const [isAddingFile, setIsAddingFile] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<BuildingFile[]>([])
  const [selectedChildrenFolder, setSelectedChildrenFolder] = useState<{
    id: AnnotationType
    label: string
  }>()
  const [nextAnnotationType, setNextAnnotationType] = useState<AnnotationType>()
  const [newSetAnnotation, setNewSetAnnotation] = useState<SetAnnotation>({
    name: '',
    memo: '',
    files: [],
    type: AnnotationType.ARCHITECTURE,
    links: [{ link: '', name: '' }]
  })
  const [isUploading, setIsUploading] = useState(false)
  const [selectedFileInfo, setSelectedFileInfo] = useState<BuildingFile | null>(
    null
  )
  const [fileDetailCache, setFileDetailCache] = useState<
    Record<number, BuildingFile>
  >({})
  const [renamingFile, setRenamingFile] = useState<BuildingFile | null>(null)
  const [previewFileIndex, setPreviewFileIndex] = useState<number | undefined>(
    undefined
  )
  const hasLinkError = useRecoilValue(hasLinkErrorState)
  const { mutate: globalMutate } = useSWRConfig()

  const [selectedChildFolder, setSelectedChildFolder] = useState<{
    id: number
    name: string
  }>()

  const containerRef = useRef<HTMLDivElement>(null)
  const [nameWidth, setNameWidth] = useState(450)
  const [sizeWidth, setSizeWidth] = useState(100)
  const resizingTarget = useRef<'name' | 'size' | null>(null)

  const FIXED_BUFFER = 332

  const getMaxNameWidth = useCallback(
    (containerWidth: number) => {
      return Math.max(50, containerWidth - FIXED_BUFFER - sizeWidth)
    },
    [sizeWidth]
  )

  useEffect(() => {
    const parent = containerRef.current?.parentElement
    if (!parent) return

    const handleResize = () => {
      const parentWidth = parent.clientWidth
      if (parentWidth > 0) {
        const maxAllowed = getMaxNameWidth(parentWidth)
        setNameWidth((prev) => Math.min(prev, maxAllowed))
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(parent)
    handleResize()
    return () => resizeObserver.disconnect()
  }, [getMaxNameWidth])

  const [sortConfig, setSortConfig] = useState<{
    key: 'name' | 'createdAt'
    order: 'asc' | 'desc'
  }>({ key: 'createdAt', order: 'desc' })

  const listRef = useRef<HTMLDivElement>(null)
  const [hasScroll, setHasScroll] = useState(false)
  const [scrollbarWidth, setScrollbarWidth] = useState(0)

  const onMouseMove = (e: MouseEvent) => {
    const target = resizingTarget.current
    if (!target) return

    const parentWidth = containerRef.current?.parentElement?.clientWidth || 510
    const totalBudget = parentWidth - FIXED_BUFFER

    if (target === 'name') {
      setNameWidth((prev) =>
        Math.max(50, Math.min(prev + e.movementX, totalBudget - sizeWidth))
      )
    } else if (target === 'size') {
      setSizeWidth((prev) =>
        Math.max(60, Math.min(prev - e.movementX, totalBudget - nameWidth))
      )
    }
  }

  const onMouseUp = () => {
    resizingTarget.current = null
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }

  const onMouseDown = (target: 'name' | 'size') => (e: React.MouseEvent) => {
    resizingTarget.current = target
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    e.preventDefault()
  }

  const handleSort = useCallback((key: 'name' | 'createdAt') => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key
      let nextOrder: 'asc' | 'desc'
      if (isSameKey) {
        nextOrder = prev.order === 'asc' ? 'desc' : 'asc'
      } else {
        nextOrder = 'desc'
      }
      return { key, order: nextOrder }
    })
  }, [])

  const annotationBgColor = useMemo(
    () => getAnnotationBgColor(newSetAnnotation.type),
    [getAnnotationBgColor, newSetAnnotation.type]
  )

  const [{ selectedSetAnnotation, annotationId }, setSelectedMode] =
    useRecoilState(setAnnotationModeState)
  const pointDetail = useRecoilValue(selectedPointDetail)
  const [, setRevalidatePointDetail] = useRecoilState(
    revalidatePointDetailState
  )
  const [, setIsRefetchingAnnotations] = useRecoilState(
    isRefetchingAnnotationsState
  )

  const { trigger: triggerAddSetAnnotation, isMutating: isAddSetAnnotation } =
    useMutation(`v1/buildings/${id}/set-annotations`, { throwOnError: true })
  const { trigger: getPresignedUrl, isMutating: isGettingPresignedUrl } =
    useMutation('v1/annotations/presigned-urls')
  const { trigger: deleteOldContent, isMutating: isDeletingOldContent } =
    useMutation('v1/delete-file')
  const { trigger: getBuildingPresignedUrl } = useMutation(
    'v1/buildings/presigned-urls'
  )
  const { trigger: saveFileMetadata } = useMutation('v1/buildings/files')
  const [memoContent, setMemoContent] = useState('')
  const { data: setAnnotationData, isValidating: isValidatingDetail } =
    useSWR<SetAnnotation>(
      selectedSetAnnotation
        ? {
            url: `v1/buildings/${id}/set-annotations/${selectedSetAnnotation}`
          }
        : null,
      {
        onSuccess: (data) => {
          const links =
            data.links && data.links.length > 0
              ? data.links
              : [{ link: '', name: '' }]
          const enrichedData = { ...data, links }
          setNewSetAnnotation((prev) => ({ ...prev, ...enrichedData }))
          setSelectedMode((prev) => ({
            ...prev,
            setAnnotationDetail: enrichedData
          }))
          form.setFieldsValue(enrichedData)
        }
      }
    )

  const { data: buildingFiles, isValidating: isValidatingBuildingFiles } =
    useSWR<ListBuildingFiles>(
      selectedChildrenFolder && isAddingFile
        ? {
            url: 'v1/buildings/files',
            params: {
              buildingId: id,
              category: selectedChildrenFolder.id
            }
          }
        : null
    )

  const { data: allBuildingFiles } = useSWR<ListBuildingFiles>(
    selectedChildrenFolder
      ? {
          url: 'v1/buildings/files',
          params: {
            buildingId: id,
            category: selectedChildrenFolder.id
          }
        }
      : null
  )

  const sortedFiles = useMemo(
    () =>
      [...(buildingFiles?.files.files || [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [buildingFiles?.files.files]
  )

  const sortedFolders = useMemo(
    () =>
      [...(buildingFiles?.files.folders || [])].sort(
        (a, b) => a.order - b.order
      ),
    [buildingFiles?.files.folders]
  )

  const { data: folderFiles } = useSWR<BuildingChildrenFolder>(
    selectedChildFolder
      ? {
          url: `v1/buildings/${id}/folders/${selectedChildFolder.id}`
        }
      : null
  )

  const sortedFolderFiles = useMemo(
    () =>
      [...(folderFiles?.files || [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [folderFiles?.files]
  )

  const folder = useMemo(
    () =>
      getBuiildingFolderList().find(
        (folder) => folder.id === newSetAnnotation.type
      ),
    [getBuiildingFolderList, newSetAnnotation.type]
  )

  const childrenFolder = useMemo(() => {
    const children = (folder?.children || []).filter(
      (item) => item.label !== '竣工図'
    )
    return [
      ...children,
      { label: 'リンク', id: 40, isFileHolder: true, children: null }
    ]
  }, [folder?.children])

  const annotationList = useMemo(() => getAnnotationList(), [getAnnotationList])

  const annotationsFile: BuildingFile[] = useMemo(() => {
    const filterList = newSetAnnotation?.files?.filter(
      (file) => file.category === selectedChildrenFolder?.id
    )
    if (isValidatingBuildingFiles || !filterList) return []

    return [...filterList].sort((a, b) => {
      const { key, order } = sortConfig
      let comparison = 0
      if (key === 'name') {
        comparison = (a.name || '').localeCompare(b.name || '')
      } else if (key === 'createdAt') {
        const dateA = dayjs(a.updatedAt || a.createdAt)
        const dateB = dayjs(b.updatedAt || b.createdAt)
        if (dateA.isSame(dateB)) {
          comparison = 0
        } else {
          comparison = dateA.isAfter(dateB) ? 1 : -1
        }
      }
      return order === 'asc' ? comparison : -comparison
    })
  }, [
    isValidatingBuildingFiles,
    newSetAnnotation?.files,
    selectedChildrenFolder?.id,
    sortConfig
  ])

  useEffect(() => {
    const checkScroll = () => {
      if (listRef.current) {
        const isScrollable =
          listRef.current.scrollHeight > listRef.current.clientHeight
        setHasScroll(isScrollable)
        setScrollbarWidth(
          listRef.current.offsetWidth - listRef.current.clientWidth
        )
      }
    }

    checkScroll()
    const timeout = setTimeout(checkScroll, 100)

    window.addEventListener('resize', checkScroll)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', checkScroll)
    }
  }, [annotationsFile, selectedChildrenFolder])

  const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']
  // Pre-fetch file detail for all images in the list for immediate info display
  useEffect(() => {
    annotationsFile.forEach((f) => {
      if (f.id <= 0) return // skip unsaved local files
      const ext =
        f.extension?.toLowerCase() || f.name.split('.').pop()?.toLowerCase()
      if (!ext || !IMAGE_EXTS.includes(ext)) return
      if (fileDetailCache[f.id]) return // already in cache
      Api.get<BuildingFile>(`/v1/buildings/files/${f.id}`)
        .then((detail) => {
          if (detail)
            setFileDetailCache((prev) => ({ ...prev, [f.id]: detail }))
        })
        .catch(() => {})
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationsFile])

  const formatBytes = (
    bytes: number | string | undefined | null,
    decimals = 1
  ) => {
    if (!bytes) return '--'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(Number(bytes)) / Math.log(k))
    return `${parseFloat((Number(bytes) / k ** i).toFixed(dm))}${sizes[i]}`
  }

  const formatFileInfo = (item: BuildingFile) => {
    const { extension, name } = item
    const ext = extension?.toLowerCase() || name.split('.').pop()?.toLowerCase()
    const { extraInfo } = item

    if (ext === 'pdf') {
      const pageCount = extraInfo?.page_count
      return pageCount ? `${pageCount}ページ` : '--'
    }

    const typesVideo = ['mov', 'mp4']
    if (ext && typesVideo.includes(ext)) {
      const duration = extraInfo?.duration
      if (duration) {
        const mins = Math.floor(duration / 60)
        const secs = Math.floor(duration % 60)
        return `${mins}:${secs.toString().padStart(2, '0')}`
      }
      return '--'
    }

    return formatBytes(extraInfo?.size || item.size)
  }

  const handleDeleteFile = (item: BuildingFile) => {
    setNewSetAnnotation((prev) => ({
      ...prev,
      files: (prev.files || []).filter((f) => f.id !== item.id)
    }))
  }

  const dropdownItems = (item: BuildingFile) => [
    { key: 'open', label: '開く', disabled: item.id < 0 },
    { key: 'rename', label: '名前を変更' },
    { key: 'download', label: 'ダウンロード', disabled: item.id < 0 },
    { key: 'info', label: 'ファイル情報', disabled: item.id < 0 },
    { key: 'delete', label: '削除' }
  ]

  // ─── processFiles must be defined BEFORE useFileDuplicateCheck ───────────────
  // We use a stable ref so the hook always calls the latest version without
  // triggering a re-render cycle, and without a forward-reference lint error.
  const processFilesRef = useRef<(files: File[], option?: number) => void>(
    () => {}
  )

  const processFiles = (files: File[], option?: number) => {
    setIsUploading(true)
    const category = selectedChildrenFolder?.id || 0
    const newFiles: BuildingFile[] = []

    files.forEach((file, index) => {
      let finalName = file.name
      let overwriteID: number | undefined
      let mode: 'overwrite' | 'keep' | undefined

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const match = duplicateFiles.find((d) => d.newFile.name === file.name)
      const effectiveOverwriteID =
        match &&
        (match.oldFile.id > 0 ? match.oldFile.id : match.oldFile.overwriteID)

      if (option === 1) {
        // Replacement mode
        if (effectiveOverwriteID) {
          overwriteID = effectiveOverwriteID
          mode = 'overwrite'
        }
      } else if (option === 2) {
        // Keep both (Numbering)
        const allExistingNames = [
          ...(allBuildingFiles?.files?.files?.map((f) => f.name) || []),
          ...(newSetAnnotation.files || []).map((f) => f.name),
          ...newFiles.map((f) => f.name)
        ]

        const lastDotIndex = file.name.lastIndexOf('.')
        const extension =
          lastDotIndex !== -1 ? file.name.substring(lastDotIndex) : ''
        const baseName =
          lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name

        let counter = 1
        while (allExistingNames.includes(finalName)) {
          finalName = `${baseName}(${counter})${extension}`
          counter += 1
        }
      }

      const localUrl = URL.createObjectURL(file)
      const newFileObj: BuildingFile = {
        id: -Date.now() - index - Math.floor(Math.random() * 1000000),
        name: finalName,
        s3Path: localUrl,
        mimeType: file.type,
        extension: finalName.split('.').pop()?.toLowerCase() || '',
        createdAt: new Date().toISOString(),
        size: file.size,
        thumbnail: localUrl,
        category,
        pointId: null,
        takenAt: dayjs(file.lastModified).format('YYYY-MM-DDTHH:mm:ssZ'),
        rawFile: file,
        overwriteID,
        mode
      }
      newFiles.push(newFileObj)
    })

    setNewSetAnnotation((prev) => {
      let filteredOldFiles = prev.files || []
      if (option === 1) {
        // Remove locally added files with the same name if overwriting
        const overwrittenNames = files.map((f) => f.name)
        filteredOldFiles = filteredOldFiles.filter(
          (f) => !overwrittenNames.includes(f.name)
        )
      }
      const updatedFiles = [...filteredOldFiles, ...newFiles]
      return {
        ...prev,
        files: updatedFiles
      }
    })

    setSelectedFiles((prev) => {
      const overwritingFiles = newFiles.filter((f) => f.mode === 'overwrite')
      if (overwritingFiles.length === 0) return prev

      const newSelected = [...prev]
      overwritingFiles.forEach((newFile) => {
        const existingIdx = newSelected.findIndex(
          (f) => f.name === newFile.name
        )
        if (existingIdx !== -1) {
          // Update the existing selection with the new file info (specifically for thumbnail/blob)
          newSelected[existingIdx] = {
            ...newSelected[existingIdx],
            ...newFile,
            id: newSelected[existingIdx].id // Keep the original ID to maintain selection if needed
          }
        } else {
          newSelected.push(newFile)
        }
      })
      return newSelected
    })

    setIsUploading(false)
  }

  // Keep the ref in sync with the latest processFiles closure on every render
  useEffect(() => {
    processFilesRef.current = processFiles
  })

  const {
    duplicateFiles,
    showDuplicateModal,
    selectedDuplicateOption,
    setSelectedDuplicateOption,
    fileInputRef: sharedFileInputRef,
    handleFileUpload,
    handleConfirmDuplicate,
    handleCancelDuplicate,
    setDuplicateFiles
  } = useFileDuplicateCheck({
    currentListFile: newSetAnnotation.files || [],
    allBuildingFiles,
    processFiles: (files: File[], option?: number) =>
      processFilesRef.current(files, option),
    allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'mov', 'mp4']
  })
  // ─────────────────────────────────────────────────────────────────────────────

  const handleDownloadFile = async (item: BuildingFile) => {
    try {
      notification.info({
        message: t('building.downloading'),
        key: 'downloading',
        icon: <LoadingOutlined className="theme-text-primary" />
      })
      const fileDetail = await Api.get<BuildingFile>(
        `/v1/buildings/files/${item.id}`
      )
      const downloadPath = fileDetail?.s3Path || item.s3Path

      const response = await fetch(downloadPath)
      if (!response.ok) throw new Error('Failed to fetch file content')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = item.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      notification.success({
        message: t('building.downloadSuccess'),
        key: 'downloading'
      })
    } catch (error: unknown) {
      notification.error({
        message: t('common.error'),
        key: 'downloading'
      })
    }
  }

  const handleMenuClick = (key: string, item: BuildingFile) => {
    switch (key) {
      case 'open': {
        const index = annotationsFile.findIndex((f) => f.id === item.id)
        setPreviewFileIndex(index)
        break
      }
      case 'rename':
        setRenamingFile(item)
        break
      case 'download':
        handleDownloadFile(item)
        break
      case 'info': {
        const detail = fileDetailCache[item.id]
        setSelectedFileInfo(detail || item)
        break
      }
      case 'delete':
        handleDeleteFile(item)
        break
      default:
        break
    }
  }

  const isEmptySetAnnotation = useMemo(
    () =>
      !newSetAnnotation?.files?.length &&
      (!newSetAnnotation?.links ||
        !newSetAnnotation.links.some((l) => l.link)) &&
      !memo,
    [newSetAnnotation?.files?.length, newSetAnnotation?.links, memo]
  )

  const onFormFocus = () => {
    stopPropagation.current = true
  }

  const onFormBlur = () => {
    stopPropagation.current = false
  }

  const handleBack = () => {
    if (hasLinkError && selectedChildrenFolder?.id === 40) return

    setSelectedChildrenFolder(undefined)
    if (selectedChildrenFolder?.id === 40) {
      const validLinks =
        inputLinks?.filter((item: LinkAnnotation) => item.link) || []
      if (validLinks.length > 0) {
        setNewSetAnnotation((prev) => ({
          ...prev,
          links: inputLinks
        }))
      } else {
        setNewSetAnnotation((prev) => prev && { ...prev, links: [] })
      }
    }
  }

  const handleCloseModal = () => {
    if (selectedSetAnnotation) {
      if (annotationId) {
        setSelectedMode({
          selectedSetAnnotation: undefined,
          setAnnotationDetail: undefined,
          annotationId: undefined,
          mode: undefined
        })
        return
      }
      setSelectedMode((prev) => ({
        ...prev,
        setAnnotationDetail: undefined,
        mode: SelectedMode.LIST_SET_ANNOTATION
      }))
      return
    }
    setSelectedMode((prev) => ({
      ...prev,
      mode: undefined,
      selectedSetAnnotation: undefined
    }))
  }

  const changeSelectedFolder = (value: AnnotationType) => {
    if (selectedSetAnnotation || !isEmptySetAnnotation) {
      setNextAnnotationType(value)
      return
    }
    if (value !== newSetAnnotation.type) {
      setNewSetAnnotation((prev) => ({
        ...prev,
        type: value
      }))
    }
  }

  const handleCloseFileSelectMenu = (clear?: boolean) => {
    if (clear) {
      setSelectedFiles(annotationsFile)
    } else {
      setNewSetAnnotation((prev) => ({
        ...prev,
        files: selectedFiles
      }))
    }
    setIsAddingFile(false)
  }

  const handleSelectFile = (file: BuildingFile) => {
    const existingEntry = selectedFiles.find(
      (item) => item.id === file.id || item.name === file.name
    )

    if (existingEntry) {
      setSelectedFiles((prev) =>
        prev.filter((item) => item.id !== existingEntry.id)
      )
      return
    }

    const overwrittenEntry = newSetAnnotation?.files?.find(
      (item) => item.name === file.name
    )

    setSelectedFiles((prev) => [
      ...prev,
      {
        ...(overwrittenEntry || file),
        category: selectedChildrenFolder?.id
      }
    ])
  }

  const handleUploadMemo = useCallback(async () => {
    if (selectedSetAnnotation && setAnnotationData?.memo) {
      await deleteOldContent({
        data: { key: Krpano.GetMemoKeyFromPresignedUrl(setAnnotationData.memo) }
      })
    }
    const newQuillInnerHtml = await Resizer.ResizedQuillImages(
      quill?.root.innerHTML || ''
    )
    const file = new Blob([newQuillInnerHtml], {
      type: 'text/plain'
    })
    if (file.size / (1024 * 1024) > 50) {
      notification.error({
        key: 'memoTooLarge',
        message: t('common.validationError.fileTooLarge')
      })
      return
    }
    const data = await getPresignedUrl({
      data: {
        extension: 'txt',
        fileName: 'annotation-content'
      }
    })
    const { key = undefined, presignedUrl = undefined } = {
      ...data?.data?.s3PreSignedLinks[0]
    }
    await axios.put(presignedUrl, file)
    return { key, content: newQuillInnerHtml }
  }, [
    deleteOldContent,
    getPresignedUrl,
    quill?.root.innerHTML,
    selectedSetAnnotation,
    setAnnotationData?.memo,
    t
  ])

  const handleAddSetAnnotation = async () => {
    if (
      isAddSetAnnotation ||
      isGettingPresignedUrl ||
      isDeletingOldContent ||
      isLoading ||
      isLoadingContent
    )
      return
    setIsLoading(true)
    try {
      const rawLinks =
        selectedChildrenFolder?.id === 40 ? inputLinks : newSetAnnotation?.links
      const links = (rawLinks || []).map((item: LinkAnnotation | null) =>
        item == null
          ? { link: '', name: '' }
          : {
              link: (item.link || '').trim(),
              name: (item.name || '').trim()
            }
      )
      let key = ''
      let content = ''
      if (newSetAnnotation.type === AnnotationType.MEMO) {
        const isMemoEmpty = Krpano.IsMemoEmpty(quill?.root.innerHTML || '')
        if (isMemoEmpty) return
        const uploadedResult = await handleUploadMemo()
        key = uploadedResult?.key || ''
        content = uploadedResult?.content || ''
      }

      let finalFiles: BuildingFile[] = newSetAnnotation?.files || []

      if (finalFiles.length > 0) {
        finalFiles = await Promise.all(
          finalFiles.map(async (fileItem: BuildingFile) => {
            if (!fileItem.rawFile) return fileItem
            const file = fileItem.rawFile
            const category = fileItem.category || 0
            // ... (rest of upload logic)
            // 0. Extract width/height for images natively before upload
            let width: number | undefined
            let height: number | undefined
            if (file.type.startsWith('image/')) {
              const img = new Image()
              img.src = URL.createObjectURL(file)
              await new Promise((resolve) => {
                img.onload = () => {
                  width = img.width
                  height = img.height
                  URL.revokeObjectURL(img.src)
                  resolve(true)
                }
                img.onerror = () => {
                  URL.revokeObjectURL(img.src)
                  resolve(true)
                }
              })
            }
            // 1. Get Presigned URL
            const presignedData = await getBuildingPresignedUrl({
              data: {
                buildingId: Number(id),
                count: 1,
                category
              }
            })
            const { presignedUrl, key: s3Key } =
              presignedData.data.s3PreSignLinks[0]
            // 2. Upload to S3
            await axios.put(presignedUrl, file)
            // 3. Save to Metadata API
            const savedFileData = await saveFileMetadata({
              data: {
                buildingId: Number(id),
                category,
                extension: fileItem.name.split('.').pop(),
                name: fileItem.name,
                mimeType: file.type,
                size: file.size,
                s3Path: s3Key,
                overwriteID: fileItem.overwriteID,
                mode: fileItem.mode,
                extraInfo:
                  width && height ? JSON.stringify({ width, height }) : null,
                takenAt: dayjs(file.lastModified).format('YYYY-MM-DDTHH:mm:ssZ')
              }
            })
            const resData = savedFileData.data
            return {
              ...fileItem,
              id: resData.id,
              name: resData.name || file.name,
              s3Path: resData.s3_path || resData.s3Path || s3Key
            }
          })
        )
      }

      if (newSetAnnotation?.files) {
        const renamedFiles = newSetAnnotation.files.filter(
          (f) => f.id > 0 && f.originalName && f.originalName !== f.name
        )
        if (renamedFiles.length > 0) {
          await Promise.all(
            renamedFiles.map((fileItem: BuildingFile) =>
              saveFileMetadata({
                method: 'patch',
                query: [fileItem.id.toString()],
                data: { name: fileItem.name }
              })
            )
          )
        }
      }

      await triggerAddSetAnnotation({
        method: selectedSetAnnotation ? 'patch' : 'post',
        data: {
          ...newSetAnnotation,
          memo: key,
          name: form.getFieldValue('name'),
          links,
          files: finalFiles.map((item: BuildingFile) => item.id)
        },
        query: selectedSetAnnotation ? [selectedSetAnnotation] : []
      })

      globalMutate({
        url: `v1/buildings/${id}/set-annotations`
      })

      // Trigger refetch point detail to update UI
      if (pointDetail?.id) {
        setIsRefetchingAnnotations(true)
        setRevalidatePointDetail(pointDetail.id)
      }

      // Update local state immediately for instant UI update
      if (selectedSetAnnotation && setAnnotationData) {
        setSelectedMode((prev) => ({
          ...prev,
          setAnnotationDetail: {
            ...setAnnotationData,
            name: form.getFieldValue('name'),
            links
          }
        }))
      }

      setIsLoading(false)
      notification.success({
        message: t('building.saved')
      })
      if (selectedSetAnnotation) {
        const krpano = document.getElementById(
          'embedpano-full'
        ) as unknown as KrpanoInterface
        pointDetail?.annotations.forEach((item) => {
          if (item.set_annotation_id === selectedSetAnnotation) {
            const headH = krpano.get(`hotspot['head-${item.id}'].ath`) as number
            const headV = krpano.get(`hotspot['head-${item.id}'].atv`) as number
            const midH = krpano.get(
              `hotspot['middle-${item.id}'].ath`
            ) as number
            const midV = krpano.get(
              `hotspot['middle-${item.id}'].atv`
            ) as number
            const h = krpano.get(`hotspot['id-${item.id}'].ath`) as number
            const v = krpano.get(`hotspot['id-${item.id}'].atv`) as number
            krpano.call(`remove_annotation(${item.id})`)
            Krpano.AddAnnotation(
              krpano,
              [
                {
                  ...item,
                  mid_h: midH,
                  mid_v: midV,
                  head_h: headH,
                  head_v: headV,
                  h,
                  v,
                  memo: content,
                  files: finalFiles,
                  links,
                  type: newSetAnnotation.type,
                  name: form.getFieldValue('name'),
                  set_annotation_name: form.getFieldValue('name')
                }
              ],
              null,
              getBuiildingType() === 10
            )
          }
        })
      }
      handleCloseModal()
    } catch (error: unknown) {
      setIsLoading(false)
    }
  }

  const checkSubmitDisabled = useCallback(() => {
    const isMemoType = newSetAnnotation.type === AnnotationType.MEMO
    const isLinkFolder = selectedChildrenFolder?.id === AnnotationType.MEMO // 40 is AnnotationType.MEMO but used for Link folder ID here

    const isMemoInvalid =
      isMemoType &&
      (!name ||
        name?.trim()?.length === 0 ||
        Krpano.IsMemoEmpty(memoContent || ''))

    const isLinkInvalid = isLinkFolder && hasLinkError

    return isMemoInvalid || isLinkInvalid
  }, [
    newSetAnnotation.type,
    selectedChildrenFolder?.id,
    name,
    memoContent,
    hasLinkError
  ])

  const handleSubmit = async () => {
    try {
      await form.validateFields()
      handleAddSetAnnotation()
    } catch (error: unknown) {
      const err = error as { errorFields?: unknown[] }
      if (err?.errorFields) {
        console.error(error)
      }
    }
  }

  const handleEnterSubmit = () => {
    if (stopPropagation.current) return
    handleSubmit()
  }

  const handleCancelChangeType = () => {
    setNextAnnotationType(undefined)
  }

  const handleConfirmChangeType = () => {
    if (nextAnnotationType) {
      setNewSetAnnotation((prev) => ({
        ...prev,
        files: [],
        links: [{ link: '', name: '' }],
        memo: '',
        type: nextAnnotationType
      }))
      form.setFieldsValue({ links: [{ link: '', name: '' }] })
      setNextAnnotationType(undefined)
    }
  }

  const handleFetchContent = useCallback(async () => {
    if (
      newSetAnnotation.type === AnnotationType.MEMO &&
      setAnnotationData?.memo &&
      quill
    ) {
      setIsLoadingContent(true)
      const textContent = await Krpano.FetchMemoContent(
        setAnnotationData.memo || ''
      )
      quill.root.innerHTML = textContent
      setIsLoadingContent(false)

      // custom placeholder of popup link
      const tooltipInput = document.querySelector(
        '.ql-tooltip input[data-link]'
      )
      if (tooltipInput) {
        tooltipInput.setAttribute('data-link', 'https://')
        tooltipInput.setAttribute('placeholder', 'https://')
      }
    }
  }, [newSetAnnotation.type, setAnnotationData?.memo, quill])

  useEffect(() => {
    setSelectedFiles(newSetAnnotation?.files || [])
  }, [newSetAnnotation.files])

  useEffect(() => {
    if (setAnnotationData?.name && !isAddingFile && !isValidatingDetail) {
      form.setFieldValue('name', setAnnotationData.name)
    }
  }, [form, isAddingFile, isValidatingDetail, setAnnotationData?.name])

  useEffect(() => {
    registerQuillImageHandler(quill, t)
  }, [quill, t])

  useEffect(() => {
    handleFetchContent()
  }, [handleFetchContent])

  useEffect(() => {
    // custom placeholder of popup link
    const tooltipInput = document.querySelector('.ql-tooltip input[data-link]')
    if (tooltipInput) {
      tooltipInput.setAttribute('data-link', 'https://')
      tooltipInput.setAttribute('placeholder', 'https://')
    }
  }, [newSetAnnotation])

  useEffect(() => {
    if (quill) {
      if (!Quill.imports['modules/blotFormatter']) {
        Quill.register('modules/blotFormatter', CustomBlotFormatter)
      }
    }
  }, [quill])

  // Register image handler with validation
  useEffect(() => {
    registerQuillImageHandler(quill, t)
  }, [quill, t])

  useEffect(() => {
    if (!quill) return

    const events = ['text-change', 'selection-change', 'editor-change']
    const handler = () => {
      if (!quill) return
      const content = quill.root.innerHTML
      setMemoContent(content)
    }

    const cleanups = events.map((event) => {
      const callback = () => handler()
      quill.on(event as 'text-change', callback)
      return () => quill.off(event as 'text-change', callback)
    })

    const inputHandler = () => handler()
    quill.root.addEventListener('input', inputHandler)

    return () => {
      cleanups.forEach((cleanup) => cleanup())
      quill.root.removeEventListener('input', inputHandler)
    }
  }, [quill, memoContent, t])

  return (
    <>
      <Modal
        width="1200px"
        rootClassName="shared-permission-modal"
        isEnterable
        onEnter={handleEnterSubmit}
        onCancel={handleCloseModal}
      >
        <Spinner spinning={isValidatingBuildingFiles || isValidatingDetail}>
          {isAddingFile && (
            <>
              <Title
                title={`${folder?.label}${'　'}${selectedChildrenFolder?.label}`}
                height={30}
              />
              <div className="h-[360px] overflow-auto">
                {!isValidatingBuildingFiles && selectedChildFolder && (
                  <>
                    <div
                      className="h-10 flex gap-5 cursor-pointer border-0 border-b border-solid border-[#EEEFF2]"
                      onClick={() => setSelectedChildFolder(undefined)}
                    >
                      <div className="my-auto">
                        <LeftArrowIcon color="#021120" />
                      </div>
                      <div className="text-[#021220] text-[18px] leading-[16px] font-medium mt-[10px]">
                        {selectedChildFolder.name}
                      </div>
                    </div>
                    {sortedFolderFiles.map((item, index) => (
                      <div
                        key={item.id}
                        className={`grid grid-cols-[auto_auto_1fr] items-center gap-5 px-5 min-h-[60px] cursor-pointer border-0 border-b ${
                          index === 0 ? 'border-t' : ''
                        } border-solid border-[#EEEFF2]`}
                        onClick={() => handleSelectFile(item)}
                      >
                        {(() => {
                          const selectedFile = selectedFiles.find(
                            (file) =>
                              file.id === item.id || file.name === item.name
                          )
                          return (
                            <>
                              <Checkbox checked={Boolean(selectedFile)} />
                              <PreviewFile file={selectedFile || item} />
                            </>
                          )
                        })()}
                        <Typography className="text-[#021120] leading-[23.17px] font-medium">
                          {item.name}
                        </Typography>
                      </div>
                    ))}
                  </>
                )}
                {!isValidatingBuildingFiles && !selectedChildFolder && (
                  <>
                    {sortedFolders.map((item) => (
                      <div
                        key={item.id}
                        className="h-[60px] flex gap-10 items-center px-10 border-0 border-b border-solid border-[#EEEFF2] cursor-pointer"
                        onClick={() =>
                          setSelectedChildFolder({
                            id: item.id,
                            name: item.name
                          })
                        }
                      >
                        <ChildrenFolderIcon />
                        <div className="text-base text-[#021120]">
                          {item.name}
                        </div>
                      </div>
                    ))}
                    {sortedFiles.map((item, index) => (
                      <div
                        key={item.id}
                        className={clsx(
                          'grid grid-cols-[auto_auto_1fr] items-center gap-5 px-5 min-h-[60px] cursor-pointer border-0 border-b border-solid border-[#EEEFF2]',
                          { 'border-t': index === 0 }
                        )}
                        onClick={() => handleSelectFile(item)}
                      >
                        {(() => {
                          const selectedFile = selectedFiles.find(
                            (file) =>
                              file.id === item.id || file.name === item.name
                          )
                          return (
                            <>
                              <Checkbox checked={Boolean(selectedFile)} />
                              <PreviewFile file={selectedFile || item} />
                            </>
                          )
                        })()}
                        <Typography className="text-[#021120] leading-[23.17px] font-medium">
                          {item.name}
                        </Typography>
                      </div>
                    ))}
                  </>
                )}
              </div>
              <div className="flex w-full pt-5 mt-7 justify-center gap-10 border-0 border-t border-solid border-[#EEEFF2]">
                <Button
                  sizing="w-[140px] h-[50px]"
                  onClick={() => handleCloseFileSelectMenu(true)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  sizing="w-[140px] h-[50px]"
                  className="theme-bg-primary"
                  onClick={() => handleCloseFileSelectMenu()}
                >
                  {t('translation.buttonSelect')}
                </Button>
              </div>
            </>
          )}

          {!isAddingFile && !isValidatingDetail && (
            <>
              <Title height={28.96} title={t('building.addSetAnnotation')} />
              <div className="flex flex-col w-full gap-[30px]">
                <div className="flex gap-5 items-center">
                  <Typography className="font-bold text-[14px] leading-[14px] text-[#021120] w-[140px] whitespace-nowrap flex gap-1 items-center">
                    {t('building.setAnnotationName')}
                    <span className="text-red-500 text-xs whitespace-nowrap">
                      ※必須
                    </span>
                  </Typography>
                  <Form form={form} onFocus={onFormFocus} onBlur={onFormBlur}>
                    <Form.Item
                      name="name"
                      className="mb-0 thin-error-message"
                      rules={[
                        {
                          validator:
                            Validators.keywordsAndLengthCustomValidator(
                              t,
                              100,
                              true,
                              true
                            )
                        }
                      ]}
                    >
                      <Input
                        className="w-60 h-[38px]"
                        placeholder="入力してください"
                      />
                    </Form.Item>
                  </Form>
                </div>

                {(!selectedChildrenFolder ||
                  newSetAnnotation.type === AnnotationType.MEMO) && (
                  <div className="flex gap-5 items-center">
                    <Typography className="font-bold text-[14px] leading-[14px] text-[#021120] w-[140px]">
                      {t('building.annotationType')}
                    </Typography>
                    <Select
                      value={newSetAnnotation.type}
                      onSelect={changeSelectedFolder}
                      options={annotationList}
                      suffixIcon={<DropdownIconCustom colorIcon="#000000" />}
                      style={{
                        width: 240,
                        fontSize: 14,
                        color: '#021120'
                      }}
                    />
                  </div>
                )}

                {newSetAnnotation.type === AnnotationType.MEMO ? (
                  <Spinner spinning={isLoadingContent}>
                    <div className="pt-[30px] px-[158px] h-[350px] mb-10">
                      <div
                        ref={quillRef}
                        className="quill-custom-editor overflow-hidden [&>#editor-resizer>.toolbar]:!hidden [&>.ql-editor]:pb-10"
                      />
                    </div>
                  </Spinner>
                ) : (
                  <div className="grow">
                    <div
                      className={`flex h-[50px] ${annotationBgColor} items-center pl-6 
  ${selectedChildrenFolder ? 'gap-5 ' : ''}
  ${
    hasLinkError && selectedChildrenFolder?.id === 40
      ? 'cursor-not-allowed opacity-50'
      : 'cursor-pointer'
  }
`}
                      onClick={handleBack}
                    >
                      {selectedChildrenFolder && <LeftArrowIcon size={20} />}
                      <Typography className="font-bold leading-[23.17px] text-white">
                        {selectedChildrenFolder
                          ? selectedChildrenFolder.label
                          : folder?.label}
                      </Typography>
                    </div>
                    {selectedChildrenFolder?.id === 40 && (
                      <Form
                        form={form}
                        labelCol={{
                          span: 5
                        }}
                        wrapperCol={{ span: 19 }}
                        labelAlign="left"
                        colon={false}
                        onFocus={onFormFocus}
                        onBlur={onFormBlur}
                        initialValues={newSetAnnotation}
                        className="flex flex-col w-full pl-[31px] pr-[50px] pt-[36px]"
                        style={{ height: '396px' }}
                        onFieldsChange={(_, allFields) => {
                          const hasError = allFields.some(
                            (f) =>
                              f.name &&
                              Array.isArray(f.name) &&
                              f.name[0] === 'links' &&
                              f.errors &&
                              f.errors.length > 0
                          )
                          setHasLinkError(hasError)
                        }}
                      >
                        <Form.List name="links">
                          {(fields, { add, remove }) => (
                            <>
                              <div
                                ref={scrollRef}
                                className="max-h-[350px] overflow-y-auto overflow-x-hidden pr-2 custom-scrollbar"
                              >
                                {fields.map(({ key, name, ...restField }) => (
                                  <div
                                    key={key}
                                    className="relative mb-4 pb-4 border-0 border-b border-[#EEEFF2] last:border-b-0 last:mb-0"
                                  >
                                    <div className="flex flex-col">
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'link']}
                                        label={t('building.inputURLAnnotation')}
                                        rules={[
                                          {
                                            validator:
                                              Validators.linkAnnotationValidator(
                                                t
                                              )
                                          }
                                        ]}
                                      >
                                        <Input
                                          placeholder="https://"
                                          className="h-[38px] rounded-[6px] py-[9px] px-[16px] text-[14px] w-full"
                                        />
                                      </Form.Item>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'name']}
                                        label={t(
                                          'building.inputNameAnnotation'
                                        )}
                                        className="!mb-1"
                                        rules={[
                                          {
                                            validator:
                                              Validators.keywordsAndLengthCustomValidator(
                                                t,
                                                500,
                                                undefined,
                                                true
                                              )
                                          }
                                        ]}
                                      >
                                        <Input
                                          placeholder={t(
                                            'common.floor.placeHolder'
                                          )}
                                          className="rounded-[6px] py-[9px] px-[16px] text-[14px] h-[38px] w-full"
                                        />
                                      </Form.Item>
                                      {name > 0 && (
                                        <div className="flex justify-end mt-0">
                                          <Button
                                            sizing="w-[84px] h-[32px] rounded-[4px]"
                                            className="!bg-[#B80808] !text-white border-0 hover:opacity-80 text-[13px] font-bold"
                                            onClick={() => remove(name)}
                                          >
                                            {t('common.delete')}
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="flex justify-center mt-4 pb-2">
                                <Button
                                  sizing="w-[160px] h-[40px]"
                                  onClick={() => add()}
                                  style={{
                                    backgroundColor: '#657cff',
                                    borderColor: '#657cff',
                                    color: '#ffffff'
                                  }}
                                >
                                  {t('translation.add')}
                                </Button>
                              </div>
                            </>
                          )}
                        </Form.List>
                      </Form>
                    )}

                    {selectedChildrenFolder?.id !== 40 &&
                      selectedChildrenFolder && (
                        <div
                          className="flex flex-col grow min-w-0 overflow-hidden"
                          ref={containerRef}
                        >
                          <div
                            className="grid items-center px-5 py-2 border-0 border-b border-solid border-[#EEEFF2] group"
                            style={{
                              gridTemplateColumns: `${nameWidth}px 4px 1fr 4px ${sizeWidth}px 80px`,
                              gap: '12px',
                              paddingRight: hasScroll
                                ? `${20 + scrollbarWidth}px`
                                : '20px'
                            }}
                          >
                            <div
                              className="flex items-center gap-1 cursor-pointer select-none"
                              onClick={() => handleSort('name')}
                            >
                              <Typography
                                className={clsx(
                                  'font-bold text-[14px] whitespace-nowrap',
                                  {
                                    'text-[#021120]': sortConfig.key === 'name',
                                    'text-[#A0A0A0]': sortConfig.key !== 'name'
                                  }
                                )}
                              >
                                名前
                                {sortConfig.key === 'name' &&
                                  (sortConfig.order === 'asc' ? '↑' : '↓')}
                              </Typography>
                            </div>
                            <div
                              onMouseDown={onMouseDown('name')}
                              className="h-full w-[4px] cursor-col-resize bg-transparent border-0 border-l border-r border-solid border-transparent group-hover:border-[#A0A0A0] transition-colors"
                            />
                            <div
                              className="flex items-center gap-1 cursor-pointer select-none"
                              onClick={() => handleSort('createdAt')}
                            >
                              <Typography
                                className={clsx(
                                  'font-bold text-[14px] whitespace-nowrap',
                                  {
                                    'text-[#021120]':
                                      sortConfig.key === 'createdAt',
                                    'text-[#A0A0A0]':
                                      sortConfig.key !== 'createdAt'
                                  }
                                )}
                              >
                                更新日
                                {sortConfig.key === 'createdAt' &&
                                  (sortConfig.order === 'asc' ? '↑' : '↓')}
                              </Typography>
                            </div>
                            <div
                              onMouseDown={onMouseDown('size')}
                              className="h-full w-[4px] cursor-col-resize bg-transparent border-0 border-l border-r border-solid border-transparent group-hover:border-[#A0A0A0] transition-colors"
                            />
                            <Typography className="text-[#021120] font-bold text-[14px] text-left">
                              サイズ
                            </Typography>
                            <div />
                          </div>

                          <div
                            ref={listRef}
                            style={{
                              height: 248,
                              overflowY: 'auto'
                            }}
                            className="overflow-x-hidden"
                          >
                            <List
                              dataSource={annotationsFile}
                              renderItem={(item) => (
                                <List.Item
                                  className="!grid items-center !px-5 min-h-[60px] cursor-pointer border-0 border-b border-solid border-[#EEEFF2] hover:bg-[#F3F6F6]"
                                  style={{
                                    gridTemplateColumns: `${nameWidth}px 4px 1fr 4px ${sizeWidth}px 80px`,
                                    gap: '12px'
                                  }}
                                >
                                  <div className="flex items-center gap-3 overflow-hidden min-w-0">
                                    <div className="w-[60px] h-[40px] shrink-0 flex items-center justify-center">
                                      <PreviewFile file={item} size="small" />
                                    </div>
                                    <Typography className="text-[#021120] text-[13px] leading-[20px] font-medium truncate">
                                      {item.name}
                                    </Typography>
                                  </div>
                                  <div />
                                  <Typography className="text-[#021120] text-[13px] font-medium whitespace-nowrap">
                                    {item.updatedAt || item.id < 0
                                      ? dayjs(
                                          item.updatedAt || item.createdAt
                                        ).format('YYYY/MM/DD')
                                      : ''}
                                  </Typography>
                                  <div />
                                  <Typography className="text-[#021120] text-[13px] font-medium whitespace-nowrap text-left">
                                    {formatFileInfo(item)}
                                  </Typography>
                                  <Dropdown
                                    menu={{
                                      items: dropdownItems(item),
                                      onClick: ({ key }) =>
                                        handleMenuClick(key, item),
                                      style: { backgroundColor: 'white' }
                                    }}
                                    trigger={['click']}
                                    overlayClassName="annotation-folder-dropdown"
                                  >
                                    <div
                                      className="flex justify-center items-center w-6 h-6 rounded hover:bg-[#3263B71A] transition-colors ml-auto cursor-pointer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <EllipsisIcon />
                                    </div>
                                  </Dropdown>
                                </List.Item>
                              )}
                            />
                          </div>
                          <div className="flex gap-10 justify-center mt-10 pb-5">
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              ref={sharedFileInputRef}
                              onChange={handleFileUpload}
                            />
                            <Button
                              sizing="w-[240px] h-[50px]"
                              className={clsx(
                                annotationBgColor,
                                'text-white border-0 hover:!text-white hover:opacity-80'
                              )}
                              onClick={() => setIsAddingFile(true)}
                            >
                              {t('building.selectFile')}
                            </Button>
                            <Button
                              sizing="w-[240px] h-[50px]"
                              className={clsx(
                                annotationBgColor,
                                'text-white border-0 hover:!text-white hover:opacity-80'
                              )}
                              onClick={() =>
                                sharedFileInputRef.current?.click()
                              }
                              loading={isUploading}
                            >
                              {t('common.upload')}
                            </Button>
                          </div>
                        </div>
                      )}

                    {selectedChildrenFolder?.id !== 40 &&
                      !selectedChildrenFolder && (
                        <List
                          style={{
                            height: 328,
                            overflow: 'auto'
                          }}
                          dataSource={childrenFolder}
                          renderItem={(item: { id: number; label: string }) => (
                            <List.Item
                              className="flex h-[60px] cursor-pointer border-0 border-b border-solid border-[#EEEFF2] !px-5"
                              onClick={() =>
                                setSelectedChildrenFolder({
                                  id: item.id as AnnotationType,
                                  label: item.label
                                })
                              }
                            >
                              <Typography className="text-[#021120] text-base leading-[23.17px] font-medium">
                                {item.label}
                              </Typography>
                              <RightArrowIcon color="#3B3C46" size={24} />
                            </List.Item>
                          )}
                        />
                      )}
                  </div>
                )}
              </div>

              <div className="flex w-full items-end pt-5 pr-[30px] mt-5 gap-10 border-0 border-t border-solid border-[#EEEFF2]">
                {selectedSetAnnotation && (
                  <Button
                    sizing="w-[140px] h-[50px]"
                    className="bg-[#B80808]"
                    onClick={() =>
                      setSelectedMode((prev) => ({
                        ...prev,
                        mode: 'deleteSetAnnotation'
                      }))
                    }
                  >
                    {t('translation.delete')}
                  </Button>
                )}
                <Button
                  sizing="w-[140px] h-[50px]"
                  className="ml-auto"
                  onClick={handleCloseModal}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="primary"
                  sizing="w-[140px] h-[50px]"
                  className={clsx(
                    'theme-bg-primary',
                    checkSubmitDisabled()
                      ? '!opacity-50 cursor-not-allowed border-0'
                      : 'hover:!opacity-80'
                  )}
                  style={
                    checkSubmitDisabled()
                      ? { pointerEvents: 'none', color: '#fff' }
                      : { color: '#fff' }
                  }
                  loading={isAddSetAnnotation || isLoading || isLoadingContent}
                  onClick={
                    checkSubmitDisabled() || isLoadingContent
                      ? undefined
                      : handleSubmit
                  }
                  aria-disabled={checkSubmitDisabled() || isLoadingContent}
                >
                  {t('common.confirm')}
                </Button>
              </div>
            </>
          )}

          {!isAddingFile && isValidatingDetail && <div className="h-[360px]" />}
        </Spinner>
      </Modal>

      {nextAnnotationType ? (
        <Modal
          width={580}
          onCancel={handleCancelChangeType}
          rootClassName="shared-permission-modal"
          isEnterable
          onEnter={handleConfirmChangeType}
        >
          <div className="flex flex-col gap-10 text-center p-5">
            <Typography className="text-[14px] leading-[21px]">
              {t('building.confirmChangeAnnotation')}
            </Typography>

            <div className="flex gap-5 mt-5">
              <Button
                sizing="w-[140px] h-[50px] mx-auto"
                onClick={handleCancelChangeType}
              >
                {t('building.buttonCancel')}
              </Button>
              <Button
                sizing="w-[140px] h-[50px] mx-auto"
                onClick={handleConfirmChangeType}
                type="primary"
              >
                {t('building.buttonSave')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {selectedFileInfo && (
        <ModalFileInfo
          file={selectedFileInfo}
          onCancel={() => setSelectedFileInfo(null)}
        />
      )}
      {renamingFile && (
        <ModalEditFileName
          file={renamingFile}
          existingNames={[
            // files already saved on server
            ...(allBuildingFiles?.files.files
              .filter((f) => f.id !== renamingFile.id)
              .map((f) => f.name) || []),
            // all files currently in the annotation list (including renamed ones and new ones)
            ...(newSetAnnotation.files || [])
              .filter((f) => f.id !== renamingFile.id)
              .map((f) => f.name)
          ]}
          onCancel={() => setRenamingFile(null)}
          onSuccess={(newName) => {
            setNewSetAnnotation((prev) => ({
              ...prev,
              files: (prev.files || []).map((f) => {
                if (f.id === renamingFile.id) {
                  const originalName = f.originalName || f.name
                  return { ...f, name: newName, originalName }
                }
                return f
              })
            }))
            setRenamingFile(null)
          }}
        />
      )}
      {previewFileIndex !== undefined && (
        <ViewerFilePreview
          fileIndex={previewFileIndex}
          file={annotationsFile[previewFileIndex]}
          maxLength={annotationsFile.length}
          setPreviewFileIndex={setPreviewFileIndex}
        />
      )}
      <DuplicateFileModal
        open={showDuplicateModal}
        duplicateFiles={duplicateFiles}
        selectedOption={selectedDuplicateOption}
        setSelectedOption={setSelectedDuplicateOption}
        onCancel={handleCancelDuplicate}
        onConfirm={handleConfirmDuplicate}
        afterClose={() => setDuplicateFiles([])}
      />
    </>
  )
}
